import React, { useRef, useState } from 'react';
import { useStore } from 'Store';
import type { PlayerData, TimeInMs } from 'Types';
import { ITEM_TYPES } from 'Utils/index';

const PROGRESS_EVENT_INTERVAL_SECONDS = 60;
const SIXTY_SECONDS_IN_MS = 60 * 1000;

const useContinueWatching = (item: PlayerData['item'], onPlayerProgress) => {
    const { appManager } = useStore();
    const [currentItem, setCurrentItem] = useState(item);
    const isAlreadyEnded = useRef(false);
    const lastProgressUpdateTimestamp = useRef(0);

    const sendProgressEvent = async (
        itemType,
        completedInPercentage: number,
        currentTime: TimeInMs,
        itemId: number | string
    ) => {
        try {
            const isSchedule = itemType === ITEM_TYPES.TV || itemType === ITEM_TYPES.RECORDING;
            const eventParams = {
                completedInPercentage: String(completedInPercentage),
                stopTimeInMillis: Math.round(currentTime),
                [isSchedule ? 'scheduleId' : 'movieId']: String(itemId)
            };

            appManager.getApi().setEvent({
                created: new Date().toISOString(),
                eventType: isSchedule ? 'CONTINUE_WATCHING_SCHEDULE' : 'CONTINUE_WATCHING_VOD',
                eventParams
            });
        } catch (error) {
            console.log(error);
        }
    };

    const sendEndEvent = async (itemType, itemId) => {
        try {
            const isSchedule = itemType === ITEM_TYPES.TV || itemType === ITEM_TYPES.RECORDING;
            const eventParams = {
                [isSchedule ? 'scheduleId' : 'movieId']: String(itemId)
            };

            appManager.getApi().setEvent({
                created: new Date().toISOString(),
                eventType: isSchedule ? 'WATCHED_SCHEDULE' : 'WATCHED_MOVIE',
                eventParams
            });
        } catch (error) {
            console.log(error);
        }
    };

    const onProgress = (progressDataObject: {
        duration: TimeInMs;
        progressData: { currentTime: TimeInMs };
    }) => {
        const {
            duration,
            progressData: { currentTime }
        } = progressDataObject;
        if (typeof onPlayerProgress === 'function') onPlayerProgress(progressDataObject);
        if (duration) {
            const isInFirst60seconds = currentTime <= PROGRESS_EVENT_INTERVAL_SECONDS;
            const isInLast60seconds =
                currentTime >= duration - PROGRESS_EVENT_INTERVAL_SECONDS &&
                duration > PROGRESS_EVENT_INTERVAL_SECONDS;
            const completedInPercentage = Math.floor((currentTime / duration) * 100);

            if ((isInFirst60seconds || isInLast60seconds) && !isAlreadyEnded.current) {
                isAlreadyEnded.current = true;
                sendEndEvent(item.modelType, item.getId());
            }

            if (
                !isInFirst60seconds &&
                !isInLast60seconds &&
                completedInPercentage > 0 &&
                completedInPercentage < 99
            ) {
                const currentTimeFloor = Math.floor(currentTime / 1000);
                isAlreadyEnded.current = false;

                if (lastProgressUpdateTimestamp.current + SIXTY_SECONDS_IN_MS < Date.now()) {
                    lastProgressUpdateTimestamp.current = Date.now();
                    sendProgressEvent(
                        item.modelType,
                        completedInPercentage,
                        currentTime,
                        item.getId()
                    );
                }
            }
        }
    };

    const onPause = (currentTime: TimeInMs, duration: TimeInMs) => {
        if (duration && duration !== -1 && !isAlreadyEnded.current) {
            const completedInPercentage = Math.floor((currentTime / duration) * 100);
            if (completedInPercentage > 0 && completedInPercentage < 99)
                sendProgressEvent(item.modelType, completedInPercentage, currentTime, item.getId());
        }
    };

    const onVideoSeek = () => {
        lastProgressUpdateTimestamp.current = 0;
    };

    const refreshDetailItem = async () => {
        try {
            const updatedItem = await appManager.getApi().fetchProgramDetail({
                scheduleId: currentItem.getId()
            });
            setCurrentItem(updatedItem);
        } catch (error) {
            console.log(error);
        }
    };

    return {
        onProgress,
        onPause,
        refreshDetailItem,
        onVideoSeek
    };
};

export default useContinueWatching;
